// src/stores/CalendarStore.js - Zustand Store for Outlook Calendar integration
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import SOCKET_EVENTS from '../services/socket/socketEvents';

// Store creation with selector subscription middleware
const useCalendarStore = create(
  subscribeWithSelector((set, get) => ({
    // ================================
    // STATE
    // ================================

    // Connection status
    calendarConnection: null,
    calendarConnectionLoading: false,

    // OAuth flow state (handles redirect to Microsoft)
    oauthUrl: null,
    oauthInitiating: false,

    // Disconnection
    disconnecting: false,

    // Connection state
    isConnected: false,

    // Calendar management
    calendars: [],
    calendarsLoading: false,
    selectedCalendarId: null,
    calendarCreating: false,
    calendarPreferenceSetting: false,

    // Cache state
    cache: {
      calendarConnection: null,
      lastFetch: null
    },

    // Internal flags
    _initialized: false,

    // ================================
    // SOCKET OPERATIONS (NO RETURN VALUES)
    // ================================

    /**
     * Load Outlook calendar connection status - NO RETURN VALUE
     */
    loadCalendarConnectionStatus: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ CalendarStore: Socket service not initialized');
        return;
      }

      console.log('🔌 CalendarStore: Loading connection status');
      set({
        calendarConnectionLoading: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_OUTLOOK_CONNECTION_STATUS, {});
    },

    /**
     * Start OAuth flow - generates OAuth URL
     * Frontend will redirect user to Microsoft
     */
    startCalendarOAuth: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ CalendarStore: Socket service not initialized');
        return;
      }

      console.log('🚀 CalendarStore: Starting OAuth flow');
      set({
        oauthInitiating: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.START_OUTLOOK_OAUTH, {});
    },

    /**
     * Disconnect Outlook calendar integration
     */
    disconnectCalendar: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ CalendarStore: Socket service not initialized');
        return;
      }

      console.log('🔌 CalendarStore: Disconnecting Outlook calendar');
      set({
        disconnecting: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.DISCONNECT_OUTLOOK_CALENDAR, {});
    },

    /**
     * List available Outlook calendars
     */
    listCalendars: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ CalendarStore: Socket service not initialized');
        return;
      }

      console.log('📅 CalendarStore: Listing calendars');
      set({
        calendarsLoading: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.LIST_OUTLOOK_CALENDARS, {});
    },

    /**
     * Create a new Outlook calendar
     */
    createCalendar: (name, color = 'auto') => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ CalendarStore: Socket service not initialized');
        return;
      }

      console.log('📅 CalendarStore: Creating calendar:', name);
      set({
        calendarCreating: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.CREATE_OUTLOOK_CALENDAR, { name, color });
    },

    /**
     * Set calendar preference for event sync
     */
    setCalendarPreference: (calendarId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ CalendarStore: Socket service not initialized');
        return;
      }

      console.log('📅 CalendarStore: Setting calendar preference:', calendarId);
      set({
        calendarPreferenceSetting: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.SET_OUTLOOK_CALENDAR_PREFERENCE, { calendar_id: calendarId });
    },

    // ================================
    // UTILITY FUNCTIONS
    // ================================

    /**
     * Get connection status string
     */
    getConnectionStatus: () => {
      const { calendarConnection } = get();
      return calendarConnection?.connection_status || 'disconnected';
    },

    /**
     * Check if connected
     */
    isCalendarConnected: () => {
      const status = get().getConnectionStatus();
      return status === 'connected';
    },

    /**
     * Check if token is expired
     */
    isTokenExpired: () => {
      const { calendarConnection } = get();
      return calendarConnection?.token_expired === true;
    },

    /**
     * Get user-friendly status message
     */
    getStatusMessage: () => {
      const status = get().getConnectionStatus();
      const messages = {
        disconnected: 'Not connected to Outlook Calendar',
        connected: 'Connected to Outlook Calendar',
        token_expired: 'Connection expired - please reconnect'
      };
      return messages[status] || 'Unknown status';
    },

    /**
     * Get connected account email
     */
    getAccountEmail: () => {
      const { calendarConnection } = get();
      return calendarConnection?.account_email || null;
    },

    /**
     * Get last sync time
     */
    getLastSync: () => {
      const { calendarConnection } = get();
      return calendarConnection?.last_sync || null;
    },

    // ================================
    // INTERNAL STATE UPDATERS (called by socket listeners)
    // ================================

    _setCalendarConnectionLoaded: (data) => {
      console.log('📡 CalendarStore: Connection status received');

      set(state => ({
        calendarConnection: data,
        calendarConnectionLoading: false,
        isConnected: data?.connection_status === 'connected',
        selectedCalendarId: data?.selected_calendar_id || null,
        cache: {
          ...state.cache,
          calendarConnection: data,
          lastFetch: new Date()
        }
      }));
    },

    _setOAuthUrlGenerated: (data) => {
      console.log('📡 CalendarStore: OAuth URL generated');

      set({
        oauthUrl: data.url,
        oauthInitiating: false
      });
    },

    _setCalendarDisconnected: (data) => {
      console.log('📡 CalendarStore: Calendar disconnected');

      set({
        calendarConnection: null,
        disconnecting: false,
        isConnected: false,
        calendars: [],
        selectedCalendarId: null,
        cache: {
          calendarConnection: null,
          lastFetch: null
        }
      });
    },

    _setCalendarsLoaded: (data) => {
      console.log('📡 CalendarStore: Calendars loaded', data);

      set({
        calendars: data.calendars || [],
        calendarsLoading: false
      });
    },

    _setCalendarCreated: (data) => {
      console.log('📡 CalendarStore: Calendar created', data);

      set(state => ({
        calendars: [...state.calendars, data.calendar],
        calendarCreating: false
      }));

      // Auto-refresh calendar list to ensure we have the latest
      setTimeout(() => {
        get().listCalendars();
      }, 500);
    },

    _setCalendarPreferenceSet: (data) => {
      console.log('📡 CalendarStore: Calendar preference set', data);

      set({
        selectedCalendarId: data.calendar_id,
        calendarPreferenceSetting: false
      });
    },

    // ================================
    // SOCKET CONNECTION MANAGEMENT (following CustomerStore pattern)
    // ================================

    _setConnectionState: (isConnected) => {
      set({ isConnected });
    },

    _getSocketManager: () => {
      // Access socket manager from window global (following pattern)
      if (typeof window !== 'undefined' && window.__socketManager) {
        return { socketManager: window.__socketManager };
      }
      return { socketManager: null };
    },

    _handleAuthReset: () => {
      console.log('🧹 CalendarStore: Resetting all state due to auth reset');

      set({
        // Reset all state to initial values
        calendarConnection: null,
        calendarConnectionLoading: false,
        oauthUrl: null,
        oauthInitiating: false,
        disconnecting: false,
        isConnected: false,
        cache: {
          calendarConnection: null,
          lastFetch: null
        },
        _initialized: false
      });
    },

    // ================================
    // INITIALIZATION (following CustomerStore pattern)
    // ================================

    _initialize: (socketManager) => {
      const state = get();

      console.log('🔌 CalendarStore: Initializing with socket manager');

      // Store socket manager reference
      if (typeof window !== 'undefined') {
        window.__socketManager = socketManager;
      }

      set({
        _initialized: true,
        isConnected: socketManager?.isAuthenticated || false
      });

      // Auto-load calendar connection status if authenticated
      if (socketManager?.isAuthenticated) {
        console.log('🔄 CalendarStore: Auto-loading calendar connection status (first time)');
        state.loadCalendarConnectionStatus();
      }
    }
  }))
);

// ================================
// SOCKET EVENT LISTENER INITIALIZATION
// ================================

/**
 * Initialize socket event listeners for CalendarStore
 * Called by SocketContext on auth success
 */
export const initializeCalendarStoreSocketListeners = (socketManager) => {
  if (!socketManager?.universalHandler) {
    console.error('❌ CalendarStore: Cannot initialize - socketManager not available');
    return;
  }

  console.log('🎧 CalendarStore: Initializing socket listeners');

  const store = useCalendarStore.getState();

  // Mark as initialized
  useCalendarStore.setState({ _initialized: true });

  // Central event handler
  const handleSocketEvent = (eventName, responseData) => {
    const store = useCalendarStore.getState();

    switch (eventName) {
      // ✅ SUCCESS EVENTS
      case SOCKET_EVENTS.OUTLOOK_CONNECTION_STATUS_LOADED:
        store._setCalendarConnectionLoaded(responseData.data);
        break;

      case SOCKET_EVENTS.OUTLOOK_OAUTH_URL_GENERATED:
        store._setOAuthUrlGenerated(responseData.data);
        break;

      case SOCKET_EVENTS.OUTLOOK_DISCONNECTED:
        store._setCalendarDisconnected(responseData.data);
        break;

      case SOCKET_EVENTS.OUTLOOK_CALENDARS_LOADED:
        store._setCalendarsLoaded(responseData.data);
        break;

      case SOCKET_EVENTS.OUTLOOK_CALENDAR_CREATED:
        store._setCalendarCreated(responseData.data);
        break;

      case SOCKET_EVENTS.OUTLOOK_CALENDAR_PREFERENCE_SET:
        store._setCalendarPreferenceSet(responseData.data);
        break;

      // ❌ ERROR EVENTS - NotificationListener handles display
      case SOCKET_EVENTS.GET_OUTLOOK_CONNECTION_STATUS_FAILED:
        useCalendarStore.setState({ calendarConnectionLoading: false });
        break;

      case SOCKET_EVENTS.START_OUTLOOK_OAUTH_FAILED:
        useCalendarStore.setState({ oauthInitiating: false });
        break;

      case SOCKET_EVENTS.DISCONNECT_OUTLOOK_CALENDAR_FAILED:
        useCalendarStore.setState({ disconnecting: false });
        console.log('❌ CalendarStore: Disconnect failed -', responseData.message);
        break;

      case SOCKET_EVENTS.LIST_OUTLOOK_CALENDARS_FAILED:
        useCalendarStore.setState({ calendarsLoading: false });
        break;

      case SOCKET_EVENTS.CREATE_OUTLOOK_CALENDAR_FAILED:
        useCalendarStore.setState({ calendarCreating: false });
        break;

      case SOCKET_EVENTS.SET_OUTLOOK_CALENDAR_PREFERENCE_FAILED:
        useCalendarStore.setState({ calendarPreferenceSetting: false });
        break;

      default:
        console.warn('⚠️ CalendarStore: Unhandled event:', eventName);
    }
  };

  // Set up individual event listeners
  const calendarEvents = [
    // Success events
    SOCKET_EVENTS.OUTLOOK_CONNECTION_STATUS_LOADED,
    SOCKET_EVENTS.OUTLOOK_OAUTH_URL_GENERATED,
    SOCKET_EVENTS.OUTLOOK_DISCONNECTED,
    SOCKET_EVENTS.OUTLOOK_CALENDARS_LOADED,
    SOCKET_EVENTS.OUTLOOK_CALENDAR_CREATED,
    SOCKET_EVENTS.OUTLOOK_CALENDAR_PREFERENCE_SET,
    // Error events
    SOCKET_EVENTS.GET_OUTLOOK_CONNECTION_STATUS_FAILED,
    SOCKET_EVENTS.START_OUTLOOK_OAUTH_FAILED,
    SOCKET_EVENTS.DISCONNECT_OUTLOOK_CALENDAR_FAILED,
    SOCKET_EVENTS.LIST_OUTLOOK_CALENDARS_FAILED,
    SOCKET_EVENTS.CREATE_OUTLOOK_CALENDAR_FAILED,
    SOCKET_EVENTS.SET_OUTLOOK_CALENDAR_PREFERENCE_FAILED
  ];

  calendarEvents.forEach(eventName => {
    socketManager.universalHandler.on(eventName, (data) => {
      handleSocketEvent(eventName, { event: eventName, data });
    });
  });

  // Set up auth reset listener
  if (typeof window !== 'undefined') {
    window.addEventListener('auth-state-reset', () => {
      useCalendarStore.getState()._handleAuthReset();
    });
  }

  // Initialize the store
  useCalendarStore.getState()._initialize(socketManager);

  console.log('✅ CalendarStore: Socket listeners initialized');
};

export default useCalendarStore;
